import { useState } from 'react';
import { Mandala } from './Mandala';
import { Plus, X, Repeat, ChevronDown, Check, Sparkles, Headphones } from 'lucide-react';

const WINDOWS = [
  { key: 'morning', label: 'Morning', hint: '6\u20139 AM' },
  { key: 'midday', label: 'Midday', hint: '11 AM\u20132 PM' },
  { key: 'evening', label: 'Evening', hint: '6\u201310 PM' },
];

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const dayLabel = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const RitualChip = ({ ritual, date, slotKey, isToday, onOpen, onRemove }) => {
  const done = ritual.percent >= 100;
  const partial = ritual.percent > 0 && ritual.percent < 100;
  return (
    <div
      className={`group relative rounded-lg border px-2.5 py-2 transition-colors duration-200
        ${done ? 'border-amber-300/50 bg-amber-300/10'
               : partial ? 'border-amber-300/30 bg-amber-300/[0.05]'
                         : 'border-white/10 bg-white/[0.03] hover:border-white/25'}`}
      data-testid={`ritual-chip-${date}-${ritual.ritual_id}`}
    >
      <button
        onClick={() => onOpen(ritual, date, slotKey)}
        disabled={!isToday && !ritual.percent}
        className="w-full text-left disabled:cursor-default"
      >
        <span className="flex items-start gap-2">
          <span className="mt-0.5 shrink-0">
            {done
              ? <Check className="w-3.5 h-3.5 text-amber-200" />
              : <span className={`block w-3 h-3 rounded-full border
                    ${partial ? 'border-amber-300/70 bg-amber-300/40' : 'border-white/25'}`} />}
          </span>
          <span className="min-w-0">
            <span className="block text-[11px] text-white leading-tight line-clamp-3 break-words">
              {ritual.title}
            </span>
            <span className="block text-[10px] text-white/40">
              {ritual.audio_url ? <Headphones className="inline w-2.5 h-2.5 mr-1 -mt-0.5" /> : null}
              {ritual.duration_minutes ? `${ritual.duration_minutes} min` : 'Open length'}
              {partial ? ` · ${ritual.percent}%` : ''}
              {ritual.needs_design ? ' · finish designing' : ''}
            </span>
          </span>
        </span>
      </button>
      {ritual.recurring && (
        <Repeat className="absolute right-1.5 bottom-1.5 w-2.5 h-2.5 text-white/20"
                title="Repeats every week" />
      )}
      {ritual.placement_id && (
        <button onClick={() => onRemove(ritual)} title="Take this out"
                className="absolute right-1 top-1 hidden rounded p-0.5 text-white/35
                           hover:text-rose-300 group-hover:block transition-colors"
                data-testid={`ritual-remove-${ritual.placement_id}`}>
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

const WindowZone = ({ day, window: slot, isToday, onAdd, onDrop, ...handlers }) => {
  const [over, setOver] = useState(false);
  const zone = day.windows?.[slot.key] || { rituals: [], percent: 0 };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const ritualId = e.dataTransfer.getData('ritual-id');
        if (ritualId) onDrop(ritualId, day.weekday, slot.key);
      }}
      className={`rounded-xl border p-2.5 space-y-2 transition-colors duration-200
        ${over ? 'border-amber-300/60 bg-amber-300/[0.07]'
               : 'border-white/[0.07] bg-black/20'}`}
      data-testid={`window-zone-${day.date}-${slot.key}`}
    >
      <div className="space-y-0.5">
        <span className="block text-[10px] uppercase tracking-[0.12em] text-white/45 truncate">
          {slot.label}
        </span>
        <span className="block text-[9px] text-white/25">
          {zone.percent > 0 ? `${zone.percent}% \u00b7 ${slot.hint}` : slot.hint}
        </span>
      </div>
      {zone.rituals.map((ritual) => (
        <RitualChip key={`${ritual.ritual_id}-${ritual.placement_id || 'done'}`}
                    ritual={ritual} date={day.date} slotKey={slot.key}
                    isToday={isToday} {...handlers} />
      ))}
      <button
        onClick={() => onAdd(day.weekday, slot.key)}
        className="w-full rounded-lg border border-dashed border-white/12 py-1.5 text-[11px]
                   text-white/35 hover:text-amber-200 hover:border-amber-300/40
                   transition-colors duration-200"
        data-testid={`window-add-${day.date}-${slot.key}`}
      >
        <Plus className="inline w-3 h-3 -mt-0.5" />
      </button>
    </div>
  );
};

const DayColumn = ({ day, today, ...rest }) => {
  const isToday = day.date === today;
  return (
    <div className={`space-y-2.5 rounded-2xl border p-3
      ${isToday ? 'border-amber-300/35 bg-amber-300/[0.04]' : 'border-white/[0.07] bg-white/[0.02]'}`}
         data-testid={`day-column-${day.date}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-white truncate">
            {DAY_NAMES[day.weekday].slice(0, 3)}
          </p>
          <p className="text-[10px] text-white/35">{dayLabel(day.date)}</p>
        </div>
        <Mandala windows={day.windows} size={40} petals={false} weekPercent={day.percent} />
      </div>
      {WINDOWS.map((slot) => (
        <WindowZone key={slot.key} day={day} window={slot} isToday={isToday} {...rest} />
      ))}
    </div>
  );
};

const DayAccordion = ({ day, today, open, onToggle, ...rest }) => {
  const isToday = day.date === today;
  return (
    <div className={`rounded-2xl border overflow-hidden
      ${isToday ? 'border-amber-300/35 bg-amber-300/[0.04]' : 'border-white/[0.07] bg-white/[0.02]'}`}
         data-testid={`day-accordion-${day.date}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3"
              data-testid={`day-toggle-${day.date}`}>
        <Mandala windows={day.windows} size={38} petals={false} weekPercent={day.percent} />
        <span className="flex-1 text-left">
          <span className="block text-sm text-white">
            {DAY_NAMES[day.weekday]}{isToday ? ' · today' : ''}
          </span>
          <span className="block text-[11px] text-white/40">
            {day.scheduled
              ? `${day.percent}% · ${WINDOWS.reduce((n, s) => n + (day.windows?.[s.key]?.rituals.length || 0), 0)} rituals`
              : 'Nothing placed yet'}
          </span>
        </span>
        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-200
                                 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2.5">
          {WINDOWS.map((slot) => (
            <WindowZone key={slot.key} day={day} window={slot} isToday={isToday} {...rest} />
          ))}
        </div>
      )}
    </div>
  );
};

const RitualPicker = ({ rituals, onPick, onClose, onCreate }) => (
  <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center
                  bg-slate-950/80 backdrop-blur-sm" data-testid="ritual-picker">
    <div className="w-full sm:max-w-md max-h-[80vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl
                    border border-white/10 bg-[#0b1020] p-6 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base md:text-lg font-semibold text-white">Place a ritual</h2>
        <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"
                data-testid="ritual-picker-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      {rituals.length === 0 && (
        <p className="text-sm text-white/45 py-4">
          Add a ritual from the library first, or write your own.
        </p>
      )}
      {rituals.map((ritual) => (
        <button key={ritual.id} onClick={() => onPick(ritual)}
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3
                           text-left hover:border-amber-300/40 transition-colors duration-200"
                data-testid={`picker-ritual-${ritual.id}`}>
          <span className="block text-sm text-white">{ritual.title}</span>
          <span className="block text-[11px] text-white/40">
            {ritual.duration_minutes ? `${ritual.duration_minutes} min` : 'Open length'}
            {ritual.intention ? ` · ${ritual.intention}` : ''}
          </span>
        </button>
      ))}
      <button onClick={onCreate}
              className="w-full rounded-xl border border-dashed border-white/20 px-4 py-3
                         text-sm text-white/70 hover:text-white hover:border-amber-300/50
                         transition-colors"
              data-testid="picker-create-custom">
        <Sparkles className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" /> Write a new ritual
      </button>
    </div>
  </div>
);

export { WINDOWS, DAY_NAMES, DayColumn, DayAccordion, RitualPicker };
